// src/components/DriverSelectorModal.js
import React, { useEffect, useRef, useState } from "react";
import { User, Navigation, MapPin, Calendar, Clock } from "lucide-react";
import axios from "../axiosConfig";
import { getSocket } from "../lib/socket";
import "../styles/DriverSelectorModal.css";
import { useTranslation } from "react-i18next";

function DriverSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedType, // "pickup" | "delivery"
  selectedPickupDelivery, // ponto selecionado (PICKUP/DELIVERY/STRATEGICPOINT)
  currentLoad,
  title,
  setIsDriverUpdating,
  onDriverAssigned, // (mantido, mesmo que não esteja a ser usado aqui)
  mode = "single", // "single" | "bulk"
  driverSlot = 1, // 1 | 2, only for normal PICKUP/DELIVERY
  allPickups = [],
  allDeliveries = [],
}) {
  
  const {
    t,
    i18n,
  } =
    useTranslation([
      "driver_selector_modal",
      "personnel",
    ]);

  const quickMode = currentLoad?.isExternalCarrier === true;

  console.log("DriverSelectorModal currentLoad:", currentLoad);
  console.log("carrierId:", currentLoad?.carrierId, "quickMode:", quickMode);

  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [alsoAssignToDeparture, setAlsoAssignToDeparture] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [assignedOverride, setAssignedOverride] = useState(null);

  // Quick manual inputs (subcontratado)
  const [quickDriverName, setQuickDriverName] = useState("");
  const [quickDriverPhone, setQuickDriverPhone] = useState("");
  const [quickError, setQuickError] = useState("");

  // dados “frescos” para o modal (server ou optimistic)
  const [freshSelected, setFreshSelected] = useState(null);
  const [freshPickups, setFreshPickups] = useState([]);
  const [freshDeliveries, setFreshDeliveries] = useState([]);

  const [loadingDrivers, setLoadingDrivers] = useState(false);

  /*
   * Personnel realtime refresh.
   *
   * The nonce reuses the existing driver/blocking fetch.
   * The ref tells that fetch not to show the initial
   * preparation loader when the refresh came from WS.
   */
  const [
    driverRefreshNonce,
    setDriverRefreshNonce,
  ] =
    useState(0);

  const silentDriverRefreshRef =
    useRef(false);

  // ===== "fun loader" (5 checkpoints) =====
  const LOADING_STEPS = 5;
  const [prepStep, setPrepStep] = useState(0); // 0..4
  const [showPrep, setShowPrep] = useState(false);

  const handleQuickAssignDriver = async () => {
    const name = String(quickDriverName || "").trim();
    const phone = String(quickDriverPhone || "").trim();

    if (!name) {
      setQuickError(
        t("quick.errors.nameRequired", { defaultValue: "Driver name is required." })
      );
      return;
    }

    setQuickError("");
    setIsDriverUpdating?.(true);

    try {
      // SINGLE
      if (mode === "single" && selectedPickupDelivery?.id) {
        const pdId = selectedPickupDelivery.id;
        const isSP = selectedPickupDelivery.type === "STRATEGICPOINT";
        const role = selectedPickupDelivery.driverRole; // "arrival" | "departure" | undefined

        if (isSP && role === "arrival") {
          await axios.put(`/pickupdeliveries/${pdId}/assign-driver-arrival`, {
            driverId: null,
            driverName: name,
            driverPhone: phone,
            driverSlot,
          });

          if (alsoAssignToDeparture) {
            await axios.put(`/pickupdeliveries/${pdId}/assign-driver-departure`, {
              driverId: null,
              driverName: name,
              driverPhone: phone,
              driverSlot,
            });
          }
        } else if (isSP && role === "departure") {
          await axios.put(`/pickupdeliveries/${pdId}/assign-driver-departure`, {
            driverId: null,
            driverName: name,
            driverPhone: phone,
            driverSlot,
          });

          if (alsoAssignToDeparture) {
            await axios.put(`/pickupdeliveries/${pdId}/assign-driver-arrival`, {
              driverId: null,
              driverName: name,
              driverPhone: phone,
              driverSlot,
            });
          }
        } else {
          await axios.put(`/pickupdeliveries/${pdId}/assign-driver`, {
            driverId: null,
            driverName: name,
            driverPhone: phone,
            type: selectedType, // "pickup" | "delivery"
            driverSlot,
          });
        }
      }

      // BULK
      if (mode === "bulk" && currentLoad?.id) {
        const url =
          selectedType === "pickup"
            ? "/pickupdeliveries/assign-driver-name-to-all-pickups"
            : "/pickupdeliveries/assign-driver-name-to-all-deliveries";
        await axios.put(url, {
          loadId: currentLoad.id,
          driverName: name,
          driverPhone: phone,
          driverSlot,
        });
      }

      onDriverAssigned?.({ driverId: null, name, phone, isManual: true });
      onClose();
    } catch (e) {
      console.error("❌ Quick driver assign failed:", e);
      setQuickError(
        t("quick.errors.failed", { defaultValue: "❌ Failed to assign this driver." })
      );
    } finally {
      setIsDriverUpdating?.(false);
    }
  };

  const dateLocale = (() => {
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    if (lng.startsWith("fr")) return "fr-FR";
    return "en-GB";
  })();

  const formatInterval = (start, end) => {
    if (!start || !end) return "";
    const opts = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" };
    try {
      return `${start.toLocaleString(dateLocale, opts)} → ${end.toLocaleString(dateLocale, opts)}`;
    } catch {
      return `${start.toLocaleString("en-GB", opts)} → ${end.toLocaleString("en-GB", opts)}`;
    }
  };

  
  const getAbsenceTypeLabel = (
    absenceType
  ) => {
    const type =
      String(
        absenceType ||
          ""
      )
        .trim()
        .toUpperCase();

    if (!type) {
      return t(
        "status.blockedBy",
        {
          defaultValue:
            "Indisponível",
        }
      );
    }

    return t(
      `personnel:absence_types.${type}`,
      {
        defaultValue:
          type,
      }
    );
  };

  const isAbsenceBlock = (
    block
  ) =>
    String(
      block?.blockType ||
        ""
    ).toUpperCase() ===
    "ABSENCE";

  const toggleExpand = (id, e) => {
    e?.stopPropagation?.();
    setExpanded((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };

  /* ---------- override persistido por contexto ---------- */
  const STORAGE_PREFIX = "tsm:driverOverride:";
  const getCtxKey = () =>
    mode === "single"
      ? `${STORAGE_PREFIX}pd:${selectedPickupDelivery?.id ?? "na"}`
      : `${STORAGE_PREFIX}load:${currentLoad?.id ?? "na"}:${selectedType}`;

  const persistOverride = (driverId) => {
    try {
      sessionStorage.setItem(getCtxKey(), String(driverId));
    } catch {}
  };
  const readOverride = () => {
    try {
      return sessionStorage.getItem(getCtxKey());
    } catch {
      return null;
    }
  };
  const clearOverride = () => {
    try {
      sessionStorage.removeItem(getCtxKey());
    } catch {}
  };

  /* ---------- reset leve quando muda o contexto ---------- */
  useEffect(() => {
    setAssignedOverride(null);
    setExpanded(new Set());
  }, [currentLoad?.id, mode, selectedType, selectedPickupDelivery?.id]);

  /* ---------- ao abrir: aplica override + fetch “sem cache” ---------- */
  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());

    if (quickMode) {
      setQuickDriverName("");
      setQuickDriverPhone("");
      setQuickError("");
      setAvailableDrivers([]);
      setLoadingDrivers(false);
      setShowPrep(false);
      setPrepStep(0);
      return;
    }

    // aplica override guardado (optimistic imediato)
    const ov = readOverride();
    if (ov != null) {
      const ovId = Number.isNaN(Number(ov)) ? ov : Number(ov);
      setAssignedOverride(ovId);

      if (mode === "single") {
        setFreshSelected((prev) => applyOptimisticToPoint(prev ?? selectedPickupDelivery, ovId));
      } else if (selectedType === "pickup") {
        setFreshPickups((prev) => {
          const src = prev?.length ? prev : allPickups;
          return src.map((p) =>
            driverSlot === 2
              ? { ...p, driver2Id: ovId }
              : { ...p, driverId: ovId }
          );
        });
      } else {
        setFreshDeliveries((prev) => {
          const src = prev?.length ? prev : allDeliveries;
          return src.map((p) =>
            driverSlot === 2
              ? { ...p, driver2Id: ovId }
              : { ...p, driverId: ovId }
          );
        });
      }
    }

    // força fresh do backend
    const noCache = { params: { _: Date.now() } };

if (mode === "single" && selectedPickupDelivery?.id) {
  axios
    .get(`/pickupdeliveries/by-id/${selectedPickupDelivery.id}`, noCache)
    .then((res) => {
      const row =
        res?.data?.pickupDelivery ||
        res?.data?.pd ||
        res?.data?.row ||
        res?.data ||
        null;

      setFreshSelected(row && !Array.isArray(row) ? row : null);
      clearOverride();
      setAssignedOverride(null);
    })
    .catch((err) =>
      console.error("Error fetching selected pickup/delivery (driver)", err)
    );
} else if (mode === "bulk" && currentLoad?.id) {
      setFreshPickups(allPickups ?? []);
      setFreshDeliveries(allDeliveries ?? []);
      clearOverride();
      setAssignedOverride(null);
    }
  }, [
    isOpen,
    quickMode,
    mode,
    selectedPickupDelivery?.id,
    currentLoad?.id,
    selectedType,
    allPickups,
    allDeliveries,
  ]);

  // Normaliza o formato vindo de /drivers/with-latest-assignments
  function normalizeDriver(raw) {
    if (!raw) return raw;

    const id =
      raw.id ??
      raw.driverId ??
      raw.driveroriginalId ??
      raw.driverOriginalId ??
      null;

    const nickname = (raw.nickname ?? raw.nick ?? raw.shortName ?? "").toString().trim();

    let fullname =
      raw.fullname ??
      raw.fullName ??
      raw.name ??
      [raw.first_name, raw.last_name].filter(Boolean).join(" ");
    fullname = (fullname || "").toString().trim();

    const homecity =
      raw.homecity ??
      raw.home_city ??
      raw.city ??
      raw.base_city ??
      raw.homebase ??
      null;

    const companyName =
      raw.company_name ??
      raw.companyName ??
      raw.company ??
      raw.employer ??
      null;

    let languages = raw.languages_spoken;
    if (typeof languages === "string") {
      const trimmed = languages.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) languages = parsed;
        } catch {
          languages = trimmed.split(/[,\s]+/).filter(Boolean);
        }
      } else {
        languages = trimmed.split(/[,\s]+/).filter(Boolean);
      }
    }
    if (!Array.isArray(languages)) languages = [];

    return {
      ...raw,
      id,
      nickname,
      fullname,
      homecity,
      company_name: companyName,
      languages_spoken: languages,
    };
  }

  /* ---------- drivers + blocking (sem cache) ---------- */
  useEffect(() => {
    if (!isOpen) return;

    if (quickMode) {
      setAvailableDrivers([]);
      setLoadingDrivers(false);
      return;
    }

    const sid =
      mode === "single"
        ? selectedPickupDelivery?.transportsessionId ?? currentLoad?.transportsessionId ?? null
        : currentLoad?.transportsessionId ?? null;

    const noCache = { params: { _: Date.now() } };

    // ✅ Só em SINGLE é que queremos "relaxar" blocks via originSessionId
    const blockingParams =
      sid && mode === "single" ? { params: { _: Date.now(), originSessionId: sid } } : noCache;

    const isSilentRefresh =
      silentDriverRefreshRef
        .current ===
      true;

    if (
      !isSilentRefresh
    ) {
      setLoadingDrivers(
        true
      );
    }

    const driversPromise = axios
      .get("/pickupdeliveries/driver-with-latest-assignments", noCache)
      .then((res) => res.data || [])
      .catch((err) => {
        console.error(
          "[DriverSelectorModal] /driver-with-latest-assignments failed:",
          err?.response?.status,
          err?.response?.data
        );
        throw err;
      });

    const blocksPromise = axios
      .get("/pickupdeliveries/driver-blocking", blockingParams)
      .then((res) => res.data || [])
      .catch((err) => {
        console.warn(
          "[DriverSelectorModal] /pickupdeliveries/driver-blocking failed (continuing without blocking):",
          err?.response?.status,
          err?.response?.data
        );
        return [];
      });

    Promise.all([driversPromise, blocksPromise])
      .then(([driversData, blockingData]) => {
        const driversWithBlocking = (driversData || []).map((raw) => {
          const driver = normalizeDriver(raw);
          const blockEntry = blockingData.find((b) => String(b.driverId) === String(driver.id));
          return { ...driver, sessionBlocks: blockEntry?.sessionBlocks || [] };
        });
        setAvailableDrivers(driversWithBlocking);
      })
      .catch((err) => {
        console.error(
          "[DriverSelectorModal] Error fetching driver data (drivers)",
          err
        );

        /*
         * On the initial load an error means we genuinely
         * do not have driver data.
         *
         * On a silent realtime refresh keep the previous
         * state instead of making the grid disappear.
         */
        if (
          !isSilentRefresh
        ) {
          setAvailableDrivers(
            []
          );
        }
      })
      .finally(() => {
        if (
          !isSilentRefresh
        ) {
          setLoadingDrivers(
            false
          );
        }

        silentDriverRefreshRef.current =
          false;
      });
  }, [
    isOpen,
    quickMode,
    mode,
    selectedPickupDelivery?.transportsessionId,
    currentLoad?.transportsessionId,
    driverRefreshNonce,
  ]);

  /*
   * =========================================================
   * PERSONNEL REALTIME → OPERATIONAL AVAILABILITY
   * =======================================================
   *
   * Only refresh while this selector is actually open.
   *
   * We also react only to the specific resource added by
   * Personnel when APPROVED/CANCELLED changes the actual
   * operational availability of drivers.
   *
   * Multiple events arriving together are coalesced into
   * one refresh.
   */

  useEffect(() => {
    if (
      !isOpen ||
      quickMode
    ) {
      return undefined;
    }

    const socket =
      getSocket();

    if (!socket) {
      return undefined;
    }

    let refreshTimer =
      null;

    const handlePersonnelChanged =
      (
        payload = {}
      ) => {
        const resources =
          Array.isArray(
            payload
              ?.resources
          )
            ? payload.resources
            : [];

        if (
          !resources.includes(
            "driverAvailability"
          )
        ) {
          return;
        }

        /*
         * Defensive guard.
         *
         * driverAvailability is currently emitted only
         * for absence availability changes.
         */
        if (
          String(
            payload
              ?.entity ||
              ""
          ).toUpperCase() !==
          "ABSENCE"
        ) {
          return;
        }

        if (
          refreshTimer
        ) {
          clearTimeout(
            refreshTimer
          );
        }

        refreshTimer =
          setTimeout(
            () => {
              /*
               * Keep current cards visible.
               *
               * The existing fetch effect performs the
               * authoritative GETs again without showing
               * the preparation loader.
               */
              silentDriverRefreshRef.current =
                true;

              setDriverRefreshNonce(
                (
                  current
                ) =>
                  current +
                  1
              );
            },
            60
          );
      };

    socket.on(
      "personnel:changed",
      handlePersonnelChanged
    );

    return () => {
      if (
        refreshTimer
      ) {
        clearTimeout(
          refreshTimer
        );
      }

      socket.off(
        "personnel:changed",
        handlePersonnelChanged
      );
    };
  }, [
    isOpen,
    quickMode,
  ]);


  // ===== Fun loader logic: advance point-to-point (no back-and-forth) =====
  useEffect(() => {
    if (!isOpen) return;

    // quick mode doesn't fetch -> no loader
    if (quickMode) {
      setShowPrep(false);
      setPrepStep(0);
      return;
    }

    let tickTimer = null;
    let doneTimer = null;

    if (loadingDrivers) {
      setShowPrep(true);
      setPrepStep(0);

      // move to step 1..3 quickly, then hold at step 3 until fetch finishes
      let s = 0;
      tickTimer = setInterval(() => {
        s += 1;
        setPrepStep((prev) => {
          const next = Math.min(3, Math.max(prev, s));
          return next;
        });
        if (s >= 3 && tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
      }, 350);
    } else {
      if (showPrep) {
        setPrepStep(4);
        doneTimer = setTimeout(() => {
          setShowPrep(false);
          setPrepStep(0);
        }, 650);
      }
    }

    return () => {
      if (tickTimer) clearInterval(tickTimer);
      if (doneTimer) clearTimeout(doneTimer);
    };
  }, [isOpen, quickMode, loadingDrivers, showPrep]);

  if (!isOpen) return null;

  /* ================= helpers de lógica ================= */
  function applyOptimisticToPoint(basePoint, driverId) {
    if (!basePoint) return basePoint;

    if (basePoint.driverRole === "arrival") {
      return driverSlot === 2
        ? { ...basePoint, driver2IdArrival: driverId }
        : { ...basePoint, driverIdArrival: driverId };
    }

    if (basePoint.driverRole === "departure") {
      return driverSlot === 2
        ? { ...basePoint, driver2IdDeparture: driverId }
        : { ...basePoint, driverIdDeparture: driverId };
    }

    return driverSlot === 2
      ? { ...basePoint, driver2Id: driverId }
      : { ...basePoint, driverId };
  }

  function getUniformAssignedDriverId(points = []) {
    const ids = points
      .map((p) => (driverSlot === 2 ? p?.driver2Id : p?.driverId) ?? null)
      .filter((id) => id != null);

    if (ids.length === 0) return null;

    return ids.every((id) => String(id) === String(ids[0])) ? ids[0] : null;
  }

  function getAssignedDriverIdForPoint(point) {
    if (!point) return null;

    if (point.driverRole === "arrival") {
      return driverSlot === 2
        ? point.driver2IdArrival ?? null
        : point.driverIdArrival ?? null;
    }

    if (point.driverRole === "departure") {
      return driverSlot === 2
        ? point.driver2IdDeparture ?? null
        : point.driverIdDeparture ?? null;
    }

    return driverSlot === 2
      ? point.driver2Id ?? null
      : point.driverId ?? null;
  }

  function isDriverAlreadyAssigned(driver, selectedPoint) {
    if (!driver || !selectedPoint) return false;

    const role = selectedPoint.driverRole;

    if (role === "arrival") {
      return driverSlot === 2
        ? Number(selectedPoint.driver2IdArrival) === Number(driver.id)
        : Number(selectedPoint.driverIdArrival) === Number(driver.id);
    }

    if (role === "departure") {
      return driverSlot === 2
        ? Number(selectedPoint.driver2IdDeparture) === Number(driver.id)
        : Number(selectedPoint.driverIdDeparture) === Number(driver.id);
    }

    return driverSlot === 2
      ? Number(selectedPoint.driver2Id) === Number(driver.id)
      : Number(selectedPoint.driverId) === Number(driver.id);
  }

  function mergePoint(base, fresh) {
    if (!fresh || Array.isArray(fresh)) return base;
    if (!base) return fresh;

    const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
    const pick = (key, fallback) => (hasOwn(fresh, key) ? fresh[key] : fallback);

    return {
      ...base,
      ...fresh,
      date: pick("date", base.date),
      hour: pick("hour", base.hour),
      type: pick("type", base.type),
      location: pick("location", base.location),
      transportsessionId: pick("transportsessionId", base.transportsessionId),

      driverId: pick("driverId", base.driverId),
      driverName: pick("driverName", base.driverName),

      driver2Id: pick("driver2Id", base.driver2Id),
      driver2Name: pick("driver2Name", base.driver2Name),

      driverIdArrival: pick("driverIdArrival", base.driverIdArrival),
      driverNameArrival: pick("driverNameArrival", base.driverNameArrival),
      driver2IdArrival: pick("driver2IdArrival", base.driver2IdArrival),
      driver2NameArrival: pick("driver2NameArrival", base.driver2NameArrival),

      driverIdDeparture: pick("driverIdDeparture", base.driverIdDeparture),
      driverNameDeparture: pick("driverNameDeparture", base.driverNameDeparture),
      driver2IdDeparture: pick("driver2IdDeparture", base.driver2IdDeparture),
      driver2NameDeparture: pick("driver2NameDeparture", base.driver2NameDeparture),

      driverRole: pick("driverRole", base.driverRole),
    };
  }

  function parseLocalPointDateTime(date, hour) {
    if (!date || !hour) return null;

    const dateOnly = String(date).includes("T")
      ? String(date).split("T")[0]
      : String(date);

    const hhmm = String(hour || "00:00").slice(0, 5).padStart(5, "0");
    const d = new Date(`${dateOnly}T${hhmm}:00`);

    return Number.isNaN(d.getTime()) ? null : d;
  }

  function getPersonnelIntervalFromPoints(
    points = []
  ) {
    const timestamps =
      points
        .map(
          (
            point
          ) =>
            parseLocalPointDateTime(
              point?.date,
              point?.hour
            )
        )
        .filter(
          Boolean
        )
        .sort(
          (
            a,
            b
          ) =>
            a.getTime() -
            b.getTime()
        );

    if (
      timestamps.length ===
      0
    ) {
      return null;
    }

    const start =
      timestamps[0];

    if (
      timestamps.length ===
      1
    ) {
      return {
        start,

        end:
          new Date(
            start.getTime() +
              1
          ),
      };
    }

    return {
      start,

      end:
        timestamps[
          timestamps.length -
            1
        ],
    };
  }

  function getPersonnelResponsibilityForCurrent(
    selectedPoint
  ) {
    if (!selectedPoint) {
      return null;
    }

    const pointType =
      String(
        selectedPoint.type ||
          ""
      ).toUpperCase();

    if (
      pointType ===
      "STRATEGICPOINT"
    ) {
      return getPersonnelIntervalFromPoints(
        [
          selectedPoint,
        ]
      );
    }

    const sortedPickups =
      [...allPickups]
        .filter(
          (
            point
          ) =>
            point?.date &&
            point?.hour
        )
        .sort(
          (
            a,
            b
          ) =>
            parseLocalPointDateTime(
              a.date,
              a.hour
            ) -
            parseLocalPointDateTime(
              b.date,
              b.hour
            )
        );

    const sortedDeliveries =
      [...allDeliveries]
        .filter(
          (
            point
          ) =>
            point?.date &&
            point?.hour
        )
        .sort(
          (
            a,
            b
          ) =>
            parseLocalPointDateTime(
              a.date,
              a.hour
            ) -
            parseLocalPointDateTime(
              b.date,
              b.hour
            )
        );

    const firstPickup =
      sortedPickups[0] ||
      null;

    const lastDelivery =
      sortedDeliveries[
        sortedDeliveries.length -
          1
      ] ||
      null;

    if (
      pointType ===
      "PICKUP"
    ) {
      return getPersonnelIntervalFromPoints(
        [
          selectedPoint,
          lastDelivery,
        ].filter(
          Boolean
        )
      );
    }

    if (
      pointType ===
      "DELIVERY"
    ) {
      return getPersonnelIntervalFromPoints(
        [
          firstPickup,
          selectedPoint,
        ].filter(
          Boolean
        )
      );
    }

    return getPersonnelIntervalFromPoints(
      [
        selectedPoint,
      ]
    );
  }

  function getPersonnelResponsibilityForBulk(
    pickups = [],
    deliveries = []
  ) {
    const sortedPickups =
      [...pickups]
        .filter(
          (
            point
          ) =>
            point?.date &&
            point?.hour
        )
        .sort(
          (
            a,
            b
          ) =>
            parseLocalPointDateTime(
              a.date,
              a.hour
            ) -
            parseLocalPointDateTime(
              b.date,
              b.hour
            )
        );

    const sortedDeliveries =
      [...deliveries]
        .filter(
          (
            point
          ) =>
            point?.date &&
            point?.hour
        )
        .sort(
          (
            a,
            b
          ) =>
            parseLocalPointDateTime(
              a.date,
              a.hour
            ) -
            parseLocalPointDateTime(
              b.date,
              b.hour
            )
        );

    return getPersonnelIntervalFromPoints(
      [
        sortedPickups[0],
        sortedDeliveries[
          sortedDeliveries.length -
            1
        ],
      ].filter(
        Boolean
      )
    );
  }

  function getBlockIntervalLocal(block) {
    const points = Array.isArray(block?.points) ? block.points : [];

    const parsedPoints = points
      .map((p) => ({
        point: p,
        dt: parseLocalPointDateTime(p?.date, p?.hour),
      }))
      .filter((x) => x.dt)
      .sort((a, b) => a.dt - b.dt);

    if (parsedPoints.length > 0) {
      const start = parsedPoints[0].dt;
      const last = parsedPoints[parsedPoints.length - 1].dt;
      const end = new Date(last.getTime() + 45 * 60 * 1000);

      return {
        start,
        end,
      };
    }

    const fallbackStart = block?.start ? new Date(block.start) : null;
    const fallbackEnd = block?.end ? new Date(block.end) : null;

    if (
      fallbackStart &&
      fallbackEnd &&
      !Number.isNaN(fallbackStart.getTime()) &&
      !Number.isNaN(fallbackEnd.getTime())
    ) {
      return {
        start: fallbackStart,
        end: fallbackEnd,
      };
    }

    return null;
  }

  function isDriverBlockedForCurrent(driver, selectedPoint) {
    if (!driver?.sessionBlocks?.length || !selectedPoint) return null;

    const sessionId = selectedPoint.transportsessionId ?? null;
    if (!selectedPoint.date || !selectedPoint.hour) return null;

    const pointStart = parseLocalPointDateTime(selectedPoint.date, selectedPoint.hour);
    if (!pointStart) return null;

    /*
     * This remains the OLD operational assignment rule.
     *
     * Do not use it for Personnel absences.
     */
    const pointEnd =
      new Date(
        pointStart.getTime() +
          45 * 60 * 1000
      );

    const personnelResponsibility =
      getPersonnelResponsibilityForCurrent(
        selectedPoint
      );

    const isSamePoint = (p, sp) => {
      if (!p || !sp) return false;

      const pDate = String(p.date || "").includes("T")
        ? String(p.date).split("T")[0]
        : String(p.date || "");

      const spDate = String(sp.date || "").includes("T")
        ? String(sp.date).split("T")[0]
        : String(sp.date || "");

      return (
        pDate === spDate &&
        String(p.hour || "").slice(0, 5) === String(sp.hour || "").slice(0, 5) &&
        String(p.type || "").toUpperCase() === String(sp.type || "").toUpperCase() &&
        String(p.location || "").trim() === String(sp.location || "").trim()
      );
    };

    for (const block of driver.sessionBlocks) {
      const blockSessionId = block.transportsessionId ?? block.sessionId ?? null;

      const isSameSession =
        blockSessionId != null &&
        sessionId != null &&
        String(blockSessionId) === String(sessionId);

      // ✅ Mantém a regra atual: não bloquear a própria sessão em single mode
      if (isSameSession) continue;

      const interval = getBlockIntervalLocal(block);
      if (!interval) continue;

      const {
        start,
        end,
      } =
        interval;

      /*
       * Personnel absence.
       *
       * IMPORTANT:
       * This uses the real transport responsibility
       * window, NOT the old point + 45 minute rule.
       */
      if (
        isAbsenceBlock(
          block
        )
      ) {
        if (
          !personnelResponsibility
        ) {
          continue;
        }

        const overlaps =
          personnelResponsibility
            .start <
            end &&
          start <
            personnelResponsibility
              .end;

        if (
          overlaps
        ) {
          return {
            isBlocked:
              true,

            block: {
              blockType:
                "ABSENCE",

              absenceId:
                block.absenceId ??
                null,

              absenceType:
                block.absenceType ??
                null,

              start,

              end,

              isFullDay:
                block.isFullDay ===
                true,

              points:
                [],
            },
          };
        }

        continue;
      }

      if (
        block.blockType === "FULL_SESSION" ||
        block.blockType === "LOAD_FULL" ||
        block.blockType === "LOAD_HALF"
      ) {
        const overlaps = pointStart < end && start < pointEnd;

        if (overlaps) {
          return {
            isBlocked: true,
            block: {
              transportsessionId: blockSessionId,
              loadId: block.loadId ?? null,
              blockType: block.blockType,
              start,
              end,
              points: block.points || [],
            },
          };
        }

        continue;
      }

      if (block.blockType === "POINT" && Array.isArray(block.points) && block.points.length) {
        for (const p of block.points) {
          const pSessionId = block.transportsessionId ?? block.sessionId ?? null;

          const isSameSessionPoint =
            pSessionId != null &&
            sessionId != null &&
            String(pSessionId) === String(sessionId);

          if (isSameSessionPoint) continue;
          if (isSamePoint(p, selectedPoint)) continue;

          const pStart = parseLocalPointDateTime(p.date, p.hour);
          if (!pStart) continue;

          const pEnd = new Date(pStart.getTime() + 45 * 60 * 1000);
          const overlaps = pointStart < pEnd && pStart < pointEnd;

          if (overlaps) {
            return {
              isBlocked: true,
              block: {
                transportsessionId: pSessionId,
                loadId: block.loadId ?? null,
                blockType: "POINT",
                start: pStart,
                end: pEnd,
                points: [p],
              },
            };
          }
        }
      }
    }

    return null;
  }

  function isDriverBlockedForBulk(driver, allPickupsArr = [], allDeliveriesArr = []) {
    if (!driver.sessionBlocks?.length) return null;

    const relevantPoints = selectedType === "pickup" ? allPickupsArr : allDeliveriesArr;
    const allPoints = relevantPoints.filter((p) => p?.date && p?.hour);
    const result = [];

    /*
     * Personnel is evaluated against the complete
     * pickup → delivery responsibility window.
     *
     * The 45-minute point rule below stays exclusively
     * for operational assignment conflicts.
     */
    const personnelResponsibility =
      getPersonnelResponsibilityForBulk(
        allPickupsArr,
        allDeliveriesArr
      );

    if (
      personnelResponsibility
    ) {
      for (
        const block
        of driver.sessionBlocks
      ) {
        if (
          !isAbsenceBlock(
            block
          )
        ) {
          continue;
        }

        const start =
          block?.start
            ? new Date(
                block.start
              )
            : null;

        const end =
          block?.end
            ? new Date(
                block.end
              )
            : null;

        if (
          !start ||
          !end ||
          Number.isNaN(
            start.getTime()
          ) ||
          Number.isNaN(
            end.getTime()
          )
        ) {
          continue;
        }

        const overlaps =
          personnelResponsibility
            .start <
            end &&
          start <
            personnelResponsibility
              .end;

        if (
          !overlaps
        ) {
          continue;
        }

        result.push({
          blockType:
            "ABSENCE",

          absenceId:
            block.absenceId ??
            null,

          absenceType:
            block.absenceType ??
            null,

          isFullDay:
            block.isFullDay ===
            true,

          start,

          end,

          points:
            [],

          causedBy:
            [],
        });
      }
    }

    for (const point of allPoints) {
      const pointTime = new Date(`${point.date}T${point.hour}:00`);
      const pointStart = pointTime;
      const pointEnd = new Date(pointTime.getTime() + 45 * 60 * 1000);

      for (const block of driver.sessionBlocks) {
        /*
         * ABSENCE was already evaluated above using
         * Personnel responsibility.
         *
         * Everything below remains the old operational
         * 45-minute blocking logic.
         */
        if (
          isAbsenceBlock(
            block
          )
        ) {
          continue;
        }

        if (
          mode === "bulk" &&
          block.transportsessionId &&
          currentLoad?.transportsessionId &&
          String(block.transportsessionId) === String(currentLoad.transportsessionId)
        ) {
          continue;
        }

        const start =
          new Date(
            block.start
          );

        const end =
          new Date(
            block.end
          );

        if (
          Number.isNaN(
            start.getTime()
          ) ||
          Number.isNaN(
            end.getTime()
          )
        ) {
          continue;
        }

        const isOverlap =
          pointStart <
            end &&
          start <
            pointEnd;

        if (isOverlap) {
          const cause = {
            type:
              point.type,

            location:
              point.location,

            date:
              point.date,

            hour:
              point.hour,
          };

          /*
           * Personnel absences are grouped by absenceId,
           * never by Transport Session.
           */
          if (
            isAbsenceBlock(
              block
            )
          ) {
            const existingAbsence =
              result.find(
                (
                  existing
                ) =>
                  existing.blockType ===
                    "ABSENCE" &&
                  String(
                    existing.absenceId
                  ) ===
                    String(
                      block.absenceId
                    )
              );

            if (
              existingAbsence
            ) {
              existingAbsence
                .causedBy
                .push(
                  cause
                );

              existingAbsence
                .causedBy
                .sort(
                  (
                    a,
                    b
                  ) =>
                    new Date(
                      `${a.date}T${a.hour}:00`
                    ) -
                    new Date(
                      `${b.date}T${b.hour}:00`
                    )
                );
            } else {
              result.push({
                blockType:
                  "ABSENCE",

                absenceId:
                  block.absenceId ??
                  null,

                absenceType:
                  block.absenceType ??
                  null,

                isFullDay:
                  block.isFullDay ===
                  true,

                start,

                end,

                points:
                  [],

                causedBy: [
                  cause,
                ],
              });
            }

            continue;
          }

          const existing =
            result.find(
              (
                existingBlock
              ) =>
                existingBlock.blockType ===
                  block.blockType &&
                (
                  block.blockType ===
                  "LOAD_FULL"
                    ? existingBlock.loadId ===
                      block.loadId
                    : existingBlock.sessionId ===
                      block.transportsessionId
                )
            );

          if (
            existing
          ) {
            existing
              .causedBy
              .push(
                cause
              );

            existing
              .causedBy
              .sort(
                (
                  a,
                  b
                ) =>
                  new Date(
                    `${a.date}T${a.hour}:00`
                  ) -
                  new Date(
                    `${b.date}T${b.hour}:00`
                  )
              );
          } else {
            result.push({
              sessionId:
                block.transportsessionId,

              loadId:
                block.loadId ??
                null,

              blockType:
                block.blockType,

              start,

              end,

              points:
                block.points ||
                [],

              causedBy: [
                cause,
              ],
            });
          }
        }
      }
    }

    if (result.length === 0) return null;

    const seen = new Set();
    const dedup = [];
    for (const block of result) {
      let key;
      if (
        block.blockType ===
        "ABSENCE"
      ) {
        key =
          `ABSENCE-${block.absenceId}`;
      } else if (
        block.blockType ===
        "FULL_SESSION"
      ) {
        key =
          `FULL_SESSION-${block.sessionId}`;
      } else if (
        block.blockType ===
          "LOAD_FULL" ||
        block.blockType ===
          "LOAD_HALF"
      ) {
        key =
          `${block.blockType}-${block.loadId}`;
      } else if (
        block.blockType ===
        "POINT"
      ) {
        key =
          `POINT-${block.loadId}-${block.start.toISOString()}`;
      }

      if (
        key &&
        !seen.has(
          key
        )
      ) {
        seen.add(
          key
        );

        dedup.push(
          block
        );
      }
    }

    const absenceBlocks =
      dedup.filter(
        (
          block
        ) =>
          block.blockType ===
          "ABSENCE"
      );

    const operationalBlocks =
      dedup.filter(
        (
          block
        ) =>
          block.blockType !==
          "ABSENCE"
      );

    const hasFullSession =
      operationalBlocks.some(
        (
          block
        ) =>
          block.blockType ===
          "FULL_SESSION"
      );

    const hasFullLoad =
      operationalBlocks.some(
        (
          block
        ) =>
          block.blockType ===
          "LOAD_FULL"
      );

    let filteredOperational =
      operationalBlocks;

    if (
      hasFullSession
    ) {
      filteredOperational =
        operationalBlocks.filter(
          (
            block
          ) =>
            block.blockType ===
            "FULL_SESSION"
        );
    } else if (
      hasFullLoad
    ) {
      filteredOperational =
        operationalBlocks.filter(
          (
            block
          ) =>
            block.blockType ===
              "LOAD_FULL" ||
            block.blockType ===
              "LOAD_HALF"
        );
    }

    const filtered = [
      ...absenceBlocks,
      ...filteredOperational,
    ];

    return {
      isBlocked:
        filtered.length >
        0,

      blocks:
        filtered,
    };
  }

  // UI helpers
  function getDriverLabel(driver) {
    const nick = (driver.nickname || "").trim();
    const full = (driver.fullname || "").trim();
    if (nick && full) return `${nick} – ${full}`;
    return nick || full || t("driver.fallback", { id: driver.id, defaultValue: `Driver #${driver.id}` });
  }

  function getDriverSubline(driver) {
    const bits = [];
    if (driver.homecity) bits.push(driver.homecity);
    if (driver.company_name) bits.push(driver.company_name);
    if (Array.isArray(driver.languages_spoken) && driver.languages_spoken.length) {
      bits.push(
        (t("driver.langPrefix", { defaultValue: "Lang:" }) + " ") +
          driver.languages_spoken.map((l) => String(l).toUpperCase()).join(", ")
      );
    }
    return bits.join(" · ");
  }

  // --- Título dinâmico (sem LOAD ID no H2) ---
  const providedTitle = (title ?? "").trim();
  const sanitizedProvidedTitle = providedTitle.replace(/\s*\(LOAD\s*#\s*[\w-]+\)\s*$/i, "");
  const baseTitle =
    sanitizedProvidedTitle ||
    (mode === "bulk"
      ? selectedType === "pickup"
        ? t("titles.bulkPickups", { defaultValue: "Assign driver to all pickups" })
        : t("titles.bulkDeliveries", { defaultValue: "Assign driver to all deliveries" })
      : (() => {
          const tp = selectedPickupDelivery?.type;
          if (tp === "PICKUP") return t("titles.singlePickup", { defaultValue: "Assign driver (Pickup)" });
          if (tp === "DELIVERY") return t("titles.singleDelivery", { defaultValue: "Assign driver (Delivery)" });
          if (tp === "STRATEGICPOINT") {
            return selectedPickupDelivery?.driverRole === "arrival"
              ? t("titles.singleArrival", { defaultValue: "Assign driver (Arrival)" })
              : selectedPickupDelivery?.driverRole === "departure"
              ? t("titles.singleDeparture", { defaultValue: "Assign driver (Departure)" })
              : t("titles.singleStrategic", { defaultValue: "Assign driver (Strategic point)" });
          }
          return t("titles.default", { defaultValue: "Assign driver" });
        })());

  const computedTitle = baseTitle;

  // ===== loader phrases (abaixo da estrada, estilo imagem) =====
  const prepLines = [
    t("prep.lines.checking", { defaultValue: "A verificar disponibilidade..." }),
    t("prep.lines.fetching", { defaultValue: "A obter motoristas..." }),
    t("prep.lines.crossing", { defaultValue: "A cruzar horários..." }),
    t("prep.lines.scoring", { defaultValue: "A preparar melhores opções..." }),
    t("prep.lines.ready", { defaultValue: "Pronto ✅" }),
  ];

  return (
    <div className="driver-modal-overlay">
      <div className="driver-modal">
        <h2 className="driver-modal-title">{computedTitle}</h2>

        <div className="driver-modal-sub">
          {t("labels.loadId", { defaultValue: "Load" })}: {currentLoad?.id ?? "-"}
        </div>

        {/* ✅ FUN LOADER (clean style como na imagem) */}
        {!quickMode && showPrep && (() => {
          // LOADING_STEPS continua 5, mas visualmente mapeamos para 3 fases
          const phase = Math.min(2, Math.floor((prepStep / Math.max(1, (LOADING_STEPS - 1))) * 3)); // 0..2
          const phaseText = [
            t("prep.lines.checking", { defaultValue: "A verificar horários..." }),
            t("prep.lines.fetching", { defaultValue: "A obter motoristas..." }),
            t("prep.lines.scoring",  { defaultValue: "A preparar melhores opções..." }),
          ][phase] || t("prep.lines.checking", { defaultValue: "A verificar horários..." });

          return (
            <div className="dsm-miniLoader" role="status" aria-live="polite">
              <div className="dsm-miniCard">
                <div className="dsm-miniIcons">
                  <div className={`dsm-miniIcon blue ${phase >= 0 ? "on" : ""}`}>
                    <User size={18} strokeWidth={2.2} />
                  </div>
                  <div className={`dsm-miniIcon purple ${phase >= 1 ? "on" : ""}`}>
                    <User size={18} strokeWidth={2.2} />
                  </div>
                  <div className={`dsm-miniIcon green ${phase >= 2 ? "on" : ""}`}>
                    <User size={18} strokeWidth={2.2} />
                  </div>
                </div>

                <div className="dsm-miniText">{phaseText}</div>

                <div className="dsm-miniDots" aria-hidden="true">
                  <span className="dsm-dot1" />
                  <span className="dsm-dot2" />
                  <span className="dsm-dot3" />
                </div>
              </div>
            </div>
          );
        })()}

        {/* ✅ Info (bulk) - ONLY after loading is done */}
        {!quickMode && mode === "bulk" && !showPrep && (
          <div className="selected-point-simple">
            <div className="point-line point-line--head">
              <span className="point-ico">
                <Navigation size={16} strokeWidth={2.2} />
              </span>
              <span className="point-label">
                {selectedType === "pickup"
                  ? t("bulk.pointsPickups", { n: allPickups.length, defaultValue: "Pickups" })
                  : t("bulk.pointsDeliveries", { n: allDeliveries.length, defaultValue: "Deliveries" })}
              </span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <MapPin size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">
                {(selectedType === "pickup" ? allPickups[0]?.location : allDeliveries[0]?.location) ||
                  t("common.unnamed", { defaultValue: "Sem nome" })}
              </strong>
            </div>

            <div className="point-line point-line--meta">
              <span className="point-ico">
                <Calendar size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {(selectedType === "pickup"
                  ? allPickups[0]?.date?.replaceAll("-", "/")
                  : allDeliveries[0]?.date?.replaceAll("-", "/")) ||
                  t("common.noDate", { defaultValue: "Sem data" })}
              </span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <Clock size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {(() => {
                  const rawHour = selectedType === "pickup" ? allPickups[0]?.hour : allDeliveries[0]?.hour;
                  if (!rawHour) return t("common.noHour", { defaultValue: "Sem hora" });
                  const [hh, mm] = String(rawHour).split(":");
                  return t("common.hourPretty", { hh, mm, defaultValue: `${hh}:${mm}` });
                })()}
              </span>
            </div>
          </div>
        )}

        {/* ✅ Info (single) - ONLY after loading is done */}
        {!quickMode && mode === "single" && selectedPickupDelivery && !showPrep && (
          <div className="selected-point-info">
            <div className="point-line point-line--head">
              <span className="point-ico">
                <Navigation size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">{selectedPickupDelivery.type}</strong>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <MapPin size={16} strokeWidth={2.2} />
              </span>
              <strong className="point-strong">
                {selectedPickupDelivery.location ||
                  t("common.unnamedLocation", { defaultValue: "Sem local" })}
              </strong>
            </div>

            <div className="point-line point-line--meta">
              <span className="point-ico">
                <Calendar size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {selectedPickupDelivery.date || t("common.noDate", { defaultValue: "Sem data" })}
              </span>
              <span className="point-sep">•</span>
              <span className="point-ico">
                <Clock size={16} strokeWidth={2.2} />
              </span>
              <span className="point-meta">
                {selectedPickupDelivery.hour || t("common.noHour", { defaultValue: "Sem hora" })}
              </span>
            </div>
          </div>
        )}

        {/* Toggle strategic (single) - ONLY after loading */}
        {!quickMode && mode === "single" && selectedPickupDelivery?.type === "STRATEGICPOINT" && !showPrep && (
          <div className="strategic-toggle">
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                id="strategic-toggle-driver"
                checked={alsoAssignToDeparture}
                onChange={(e) => setAlsoAssignToDeparture(e.target.checked)}
              />
              <label htmlFor="strategic-toggle-driver" className="toggle-label" />
              <span className="toggle-text">
                {selectedPickupDelivery?.driverRole === "arrival"
                  ? t("toggles.alsoDeparture", { defaultValue: "Atribuir também à partida" })
                  : selectedPickupDelivery?.driverRole === "departure"
                  ? t("toggles.alsoArrival", { defaultValue: "Atribuir também à chegada" })
                  : ""}
              </span>
            </div>
          </div>
        )}

        {/* ======== QUICK MODE (subcontratado) ======== */}
        {quickMode ? (
          <div className="driver-quick-wrap">
            <div className="driver-quick-card" onClick={(e) => e.stopPropagation()}>
              <div className="driver-quick-head">
                <div className="driver-quick-head-left">
                  <span className="driver-quick-head-icon">
                    <User size={20} strokeWidth={2} />
                  </span>
                  <div className="driver-quick-head-texts">
                    <div className="driver-quick-head-title">
                      {t("quick.title", { defaultValue: "Quick driver assignment" })}
                    </div>
                    <div className="driver-quick-head-sub">
                      {t("quick.skipBlocking", {
                        defaultValue: "A subcontracted carrier is assigned to this load — using manual driver input.",
                      })}
                    </div>
                  </div>
                </div>
                <span className="driver-quick-head-badge" title="Info">
                  i
                </span>
              </div>

              <div className="driver-quick-fields">
                <div className="driver-quick-field">
                  <label className="driver-quick-label">
                    {t("quick.name", { defaultValue: "Driver name" })}
                  </label>
                  <input
                    className={`driver-quick-input ${quickError ? "input-error" : ""}`}
                    value={quickDriverName}
                    onChange={(e) => {
                      setQuickDriverName(e.target.value);
                      if (quickError) setQuickError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleQuickAssignDriver();
                    }}
                    placeholder={t("quick.namePh", { defaultValue: "e.g. João Silva" })}
                  />
                </div>

                <div className="driver-quick-field">
                  <label className="driver-quick-label">
                    {t("quick.phone", { defaultValue: "Driver phone (optional)" })}
                  </label>
                  <input
                    className="driver-quick-input"
                    value={quickDriverPhone}
                    onChange={(e) => setQuickDriverPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleQuickAssignDriver();
                    }}
                    placeholder={t("quick.phonePh", { defaultValue: "e.g. +351 912 345 678" })}
                  />
                </div>

                {quickError && <div className="driver-quick-error">{quickError}</div>}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ✅ skeleton grid enquanto showPrep (abre bonito, não fica “vazio”) */}
            {showPrep && (
              <div className="driver-grid dsm-skel-grid" aria-hidden="true">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="dsm-skel-card">
                    <div className="dsm-skel-top">
                      <div className="dsm-skel-ico" />
                      <div className="dsm-skel-bar w1" />
                      <div className="dsm-skel-dot" />
                    </div>
                    <div className="dsm-skel-bar w2" />
                    <div className="dsm-skel-bar w3" />
                  </div>
                ))}
              </div>
            )}

            {/* ======== GRID ======== */}
            {!showPrep &&
              (() => {
                const effectiveSelected = mergePoint(selectedPickupDelivery, freshSelected);
                const effectivePickups = freshPickups?.length ? freshPickups : allPickups;
                const effectiveDeliveries = freshDeliveries?.length ? freshDeliveries : allDeliveries;

                const sorted = [...availableDrivers].sort((a, b) => {
                  const aAssigned = mode === "single" && isDriverAlreadyAssigned(a, effectiveSelected);
                  const bAssigned = mode === "single" && isDriverAlreadyAssigned(b, effectiveSelected);

                  const aConflict =
                    mode === "bulk"
                      ? isDriverBlockedForBulk(a, effectivePickups, effectiveDeliveries)
                      : isDriverBlockedForCurrent(a, effectiveSelected);
                  const bConflict =
                    mode === "bulk"
                      ? isDriverBlockedForBulk(b, effectivePickups, effectiveDeliveries)
                      : isDriverBlockedForCurrent(b, effectiveSelected);

                  const aBlocked = !!aConflict?.isBlocked || (mode === "single" && aAssigned);
                  const bBlocked = !!bConflict?.isBlocked || (mode === "single" && bAssigned);

                  const aLabel = getDriverLabel(a);
                  const bLabel = getDriverLabel(b);

                  if (aAssigned && !bAssigned) return -1;
                  if (!aAssigned && bAssigned) return 1;
                  if (aBlocked && !bBlocked) return 1;
                  if (!aBlocked && bBlocked) return -1;
                  return aLabel.localeCompare(bLabel);
                });

                let assignedDriverId =
                  assignedOverride ??
                  (mode === "single"
                    ? getAssignedDriverIdForPoint(effectiveSelected)
                    : getUniformAssignedDriverId(selectedType === "pickup" ? effectivePickups : effectiveDeliveries));

                const assignedDriver =
                  assignedDriverId != null ? sorted.find((d) => String(d.id) === String(assignedDriverId)) : null;

                const gridDrivers = assignedDriver
                  ? sorted.filter((d) => String(d.id) !== String(assignedDriverId))
                  : sorted;

                return (
                  <div className="driver-grid">
                    {assignedDriver && (
                      <div className="selected-driver-banner">
                        <div className="banner-left">
                          <span className="banner-icon">
                            <User size={26} strokeWidth={2.2} />
                          </span>
                          <div className="banner-texts">
                            <div className="banner-name">{getDriverLabel(assignedDriver)}</div>
                            <div className="banner-sub">
                              {getDriverSubline(assignedDriver) ||
                                (mode === "bulk"
                                  ? t("status.alreadyAssignedLoad", { defaultValue: "Já atribuído nesta carga" })
                                  : t("status.alreadyAssigned", { defaultValue: "Já atribuído neste ponto" }))}
                            </div>
                          </div>
                        </div>
                        <div className="banner-right">
                          <span className="banner-check">✓</span>
                        </div>
                      </div>
                    )}

                    {gridDrivers.map((driver) => {
                      const conflict =
                        mode === "bulk"
                          ? isDriverBlockedForBulk(driver, effectivePickups, effectiveDeliveries)
                          : isDriverBlockedForCurrent(driver, effectiveSelected);

                      const alreadyAssigned = mode === "single" ? isDriverAlreadyAssigned(driver, effectiveSelected) : false;
                      const isBlocked = !!conflict?.isBlocked || (mode === "single" && alreadyAssigned);
                      const isExpanded = expanded.has(driver.id);

                      const absenceBlock =
                        mode ===
                        "bulk"
                          ? conflict
                              ?.blocks
                              ?.find(
                                isAbsenceBlock
                              ) ||
                            null
                          : isAbsenceBlock(
                              conflict
                                ?.block
                            )
                            ? conflict
                                .block
                            : null;

                      const absenceLabel =
                        absenceBlock
                          ? getAbsenceTypeLabel(
                              absenceBlock
                                .absenceType
                            )
                          : null;

                      const statusText =
                        alreadyAssigned
                          ? t(
                              "status.alreadyAssigned",
                              {
                                defaultValue:
                                  "Já atribuído",
                              }
                            )
                          : absenceLabel
                            ? `${t(
                                "status.blockedBy",
                                {
                                  defaultValue:
                                    "Indisponível",
                                }
                              )} · ${absenceLabel}`
                            : isBlocked
                              ? t(
                                  "status.blockedBy",
                                  {
                                    defaultValue:
                                      "Indisponível",
                                  }
                                )
                              : t(
                                  "status.available",
                                  {
                                    defaultValue:
                                      "Disponível",
                                  }
                                );

                      return (
                        <div className="driver-grid-item" key={driver.id}>
                          <div
                            className={`driver-card ${isBlocked ? "blocked" : ""}`}
                            onClick={() => {
                              if (isBlocked) return;

                              setIsDriverUpdating?.(true);
                              setAssignedOverride(driver.id);
                              persistOverride(driver.id);

                              if (mode === "single") {
                                setFreshSelected((prev) =>
                                  applyOptimisticToPoint(prev ?? selectedPickupDelivery, driver.id)
                                );
                              } else {
                                if (selectedType === "pickup") {
                                  setFreshPickups((prev) => {
                                    const src = prev && prev.length ? prev : allPickups;
                                    return src.map((p) => ({ ...p, driverId: driver.id }));
                                  });
                                } else {
                                  setFreshDeliveries((prev) => {
                                    const src = prev && prev.length ? prev : allDeliveries;
                                    return src.map((p) => ({ ...p, driverId: driver.id }));
                                  });
                                }
                              }

                              Promise.resolve(
                                onSelect(driver, selectedType, alsoAssignToDeparture, driverSlot)
                              ).catch(() => {});
                              onClose();
                            }}
                          >
                            <div className="driver-card-top">
                              <div className="driver-left">
                                <span className="driver-icon">
                                  <User size={20} strokeWidth={2} />
                                </span>
                                <span className="driver-name">{getDriverLabel(driver)}</span>
                                <span className={`driver-dot ${isBlocked ? "red" : "green"}`} />
                              </div>

                              {isBlocked && (
                                <button
                                  className={`caret ${isExpanded ? "open" : ""}`}
                                  onClick={(e) => toggleExpand(driver.id, e)}
                                  aria-label={t("aria.toggleBlockDetails", { defaultValue: "Toggle details" })}
                                />
                              )}
                            </div>

                            <div className={`driver-status ${isBlocked ? "blocked" : "ok"}`}>{statusText}</div>

                            {getDriverSubline(driver) && <div className="driver-extra">{getDriverSubline(driver)}</div>}

                            {isBlocked && (
                              <div
                                className={`block-details ${isExpanded ? "show" : ""}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* SINGLE */}
                                {mode === "single" && conflict?.block && (
                                  <div className="blocked-msg">
                                    🚫{" "}
                                    {(() => {
                                      const blk = conflict.block;
                                      if (
                                        isAbsenceBlock(
                                          blk
                                        )
                                      ) {
                                        return `${t(
                                          "status.blockedBy",
                                          {
                                            defaultValue:
                                              "Indisponível",
                                          }
                                        )} · ${getAbsenceTypeLabel(
                                          blk.absenceType
                                        )}`;
                                      }
                                      if (blk.blockType === "LOAD_FULL")
                                        return t("blocks.loadFull", { id: blk.loadId ?? "?", defaultValue: "Bloqueado (load completa)" });
                                      if (blk.blockType === "LOAD_HALF") {
                                        const which =
                                          blk.points?.[0]?.type === "PICKUP"
                                            ? t("blocks.allPickups", { defaultValue: "todas as recolhas" })
                                            : t("blocks.allDeliveries", { defaultValue: "todas as entregas" });
                                        return t("blocks.loadHalf", { id: blk.loadId ?? "?", which, defaultValue: `Bloqueado (${which})` });
                                      }
                                      if (blk.blockType === "POINT") {
                                        const p0 = blk.points?.[0] || {};
                                        return t("blocks.singleAssignment", {
                                          type: p0.type ?? "?",
                                          location: p0.location ?? "?",
                                          defaultValue: "Bloqueado por outro ponto",
                                        });
                                      }
                                      return t("blocks.session", { id: blk.transportsessionId, defaultValue: "Bloqueado por sessão" });
                                    })()}

                                    <div className="blocked-interval">⏱ {formatInterval(conflict.block.start, conflict.block.end)}</div>

                                    {conflict.block.points?.length > 0 && (
                                      <ul className="blocked-points-list">
                                        {conflict.block.points.map((p, i) => (
                                          <li key={i}>
                                            • {p.type} {t("common.at", { defaultValue: "em" })} {p.location || "-"} – {p.date} {p.hour}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}

                                {/* BULK */}
                                {mode === "bulk" && conflict?.blocks?.length > 0 && (
                                  <div className="blocked-msg">
                                    {conflict.blocks.map((blk, idx) => (
                                      <div key={idx} className="bulk-block">
                                        •{" "}
                                        {(() => {
                                          if (
                                            isAbsenceBlock(
                                              blk
                                            )
                                          ) {
                                            return `${t(
                                              "status.blockedBy",
                                              {
                                                defaultValue:
                                                  "Indisponível",
                                              }
                                            )} · ${getAbsenceTypeLabel(
                                              blk.absenceType
                                            )}`;
                                          }
                                          if (blk.blockType === "FULL_SESSION")
                                            return t("blocks.session", { id: blk.sessionId, defaultValue: "Bloqueado por sessão" });
                                          if (blk.blockType === "LOAD_FULL")
                                            return t("blocks.loadFull", { id: blk.loadId ?? "?", defaultValue: "Bloqueado (load completa)" });
                                          if (blk.blockType === "LOAD_HALF") {
                                            const which =
                                              blk.points?.[0]?.type === "PICKUP"
                                                ? t("blocks.allPickups", { defaultValue: "todas as recolhas" })
                                                : t("blocks.allDeliveries", { defaultValue: "todas as entregas" });
                                            return t("blocks.loadHalf", { id: blk.loadId ?? "?", which, defaultValue: `Bloqueado (${which})` });
                                          }
                                          if (blk.blockType === "POINT") {
                                            const p0 = blk.points?.[0] || {};
                                            return t("blocks.singleAssignment", {
                                              type: p0.type ?? "?",
                                              location: p0.location ?? "?",
                                              defaultValue: "Bloqueado por outro ponto",
                                            });
                                          }
                                          return t("blocks.unknown", { defaultValue: "Bloqueado" });
                                        })()}

                                        <div className="blocked-interval">⏱ {formatInterval(blk.start, blk.end)}</div>

                                        {blk.causedBy?.length > 0 && (
                                          <div className="caused-by-msg">
                                            {blk.causedBy.map((c, i) => (
                                              <div key={i}>
                                                ⚠️ {t("blocks.causedBy", { defaultValue: "Causado por" })} {c.type}{" "}
                                                {t("common.at", { defaultValue: "em" })} <strong>{c.location || "-"}</strong> — {c.date}{" "}
                                                {c.hour}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {blk.points?.length > 0 && (
                                          <ul className="blocked-points-list">
                                            {blk.points.map((p, i) => (
                                              <li key={i}>
                                                • {p.type} {t("common.at", { defaultValue: "em" })} {p.location || "-"} – {p.date} {p.hour}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
          </>
        )}

        {quickMode ? (
          <div className="driver-modal-actions">
            <button onClick={onClose} className="driver-modal-btn driver-modal-btn--secondary">
              {t("buttons.close", { defaultValue: "Close" })}
            </button>
            <button className="driver-modal-btn driver-modal-btn--primary" onClick={handleQuickAssignDriver}>
              {t("quick.assign", { defaultValue: "Assign" })}
            </button>
          </div>
        ) : (
          <button onClick={onClose} className="driver-modal-close">
            {t("buttons.close", { defaultValue: "Close" })}
          </button>
        )}
      </div>
    </div>
  );
}

export default DriverSelectorModal;